<template>
    <b-container>
        <b-card v-if="wardId !==null">
            <b-breadcrumb>
            <b-breadcrumb-item :to="{name: 'ward-list', params: { wardId: wardId}}">Back to {{ward.ward_name}}</b-breadcrumb-item>
            <b-breadcrumb-item active>Bulk Contact Uploads</b-breadcrumb-item>
            </b-breadcrumb>
        </b-card>
        <b-card class="mt-3">
            <b-row>
                <b-col>
                <b-button
                    :to="{name: 'ward-import-utility', params: {'wardId': wardId}}"
                    class="float-right"
                    variant="primary">
                    <font-awesome-icon icon="file-import" /> Load Excel File
                </b-button>
                </b-col>
                    
            </b-row>
            <b-row>
                <b-col>
                    <p>
                        You can bulk import records from an excel file into your ward in 3 steps
                        <ol>
                            <li>Click on <strong>Load Excel File</strong> button and follow directions</li>
                            <li>From the list below filter for <strong>Pending</strong> files and view your file, then Process the spreadsheet</li>
                            <li>From the list below filter for <strong>Finished</strong> files and view your file, then Send SMS for created contacts</li>
                        </ol>

                    </p>
                </b-col>
                <b-col>
                <b-form-group
                 id="import-state"
                 label="Spreadsheet Processing State"
                 label-for="import-state"
                 description="Processing state to filter">
                 <b-form-select id="import-state" v-model="importState" :options="importStates" @change="refresImportRequestList()"></b-form-select>
                </b-form-group>
                <b-button
                    @click="refresImportRequestList"
                    class="float-right"
                    variant="secondary">
                    <font-awesome-icon icon="redo" />
                </b-button>                
                </b-col>
            </b-row>
            <b-row>
            <b-table
                :fields="table.fields"
                :items="table.items"
                :busy="table.isBusy"
                class="mb-3 mt-3"
                striped
                responsive
                show-empty
                empty-text="No Import Files"
                outlined>
                <template #table-busy>
                  <div class="text-center text-danger my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Loading...</strong>
                  </div>
                </template> 
                <template #cell(created_on)="row">
                    <span :id="'tooltip-created-import' + row.index">{{$moment.utc(row.item.created_on).local().fromNow()}}</span>
                    <b-tooltip :target="'tooltip-created-import' + row.index" triggers="hover">
                        {{$moment.utc(row.item.created_on).local().format('llll')}}
                    </b-tooltip>                       

                </template>                                           
                <template v-slot:cell(Action)="row">
                    <b-button 
                        :key="row.item.uid" 
                        :to="{name: 'ward-bulk-import-detail', params: { 'wardId': wardId, 'importRequestId': row.item.bulk_import_id}}" 
                        variant="outline-secondary"> 
                    <font-awesome-icon icon="eye" />
                    </b-button>
                </template>
                <template #cell(import_status)="row">
                    <b-badge :variant="chooseColor(row.item.import_status)">{{row.item.import_status}}</b-badge>
                </template>
            </b-table>
            </b-row>
        <b-row>
            <b-col align="center">
                <b-button-group v-if="pages.length>1">
                    <b-button @click="searchImportRequests(currentPage-1)">Prev</b-button>
                    <b-button @click="searchImportRequests(currentPage+1)">Next</b-button>
                </b-button-group>                
            </b-col>                
        </b-row>
        </b-card>
    </b-container>
</template>
<script>
import { WardApi } from "@/app/wards/wardApi";
import handleError from "@/errorHandler";

export default {
    name: 'WardImportRequestList',
    props: {
        wardId: {
            type: String,
            required: false
        },
        initImportState: {
            type: String,
            required: false,
            default: 'PENDING'
        }
    },
    computed: {},
    data () {
        return {
            state_colors: {
                'PENDING': 'secondary',
                'PROCESSING': 'warning',
                'FINISHED': 'success',
                'FAILED': 'danger'
            },            
            importStates: [],
            importState: 'PENDING',
            ward: {
                ward_name: null
            },
            pages: [],
            nextPage: {
                aux_id: null,
                pk_id: null,
                gsi_pk: null,
                gsi_sk: null
            },            
            table: {
                id: "bulk-import-list",
                fields: [
                    {
                        key: 'original_file_name',
                        label: 'Excel Filename'
                    },                    
                    {
                        key: 'file_has_header',
                        label: 'File has a header'
                    },
                    {
                        key: 'number_of_contacts',
                        label: 'Sheet Contacts'
                    },
                    {
                        key: 'import_status',
                        label: 'Processing Status'
                    },
                    {
                        key: 'created_on',
                        label: 'Updated'
                    },                    
                    {
                        key: "Action",
                        label: "View"
                    }
                ],
                items: [],
                isBusy: false,
                totalRows: 0,
                page: {
                    index: 1,
                    size: 5
                }
            }
        }
    },
    async mounted() {
        this.importStates = this.$store.state.frontEndSettings.import_states;
        this.importState = this.initImportState;
        if(this.wardId!==null)
            this.ward = await this.getWard();
        this.pages.push(this.nextPage);
        await this.searchImportRequests(0);            
    },
    methods: {
        chooseColor(theStatus) {
            return this.state_colors[theStatus];
        },        
        async getWard() {
            try{
                const { data } = await WardApi.getWardDetail(this.wardId);
                return data.ward;
            } catch(e) {
                handleError(e, "Error trying to get Ward Information");
            }
        },
        async searchImportRequests(page) {
            if(page < 0) {
                return;
            }
            if(page > this.pages.length - 1) {
                return;
            }
            const payload =  this.pages[page];
            payload.import_state = this.importState;
            this.table.isBusy = true;
            try {
                const { data } = await WardApi.searchImportRequests(this.wardId, payload);
                if (data.last_evaluated_key !== null) {
                    if(!this.existInPageList(data.last_evaluated_key, this.pages))
                        this.pages.push(data.last_evaluated_key); 
                }
                this.table.items = data.bulk_list;
                this.table.isBusy = false;
                this.currentPage = page;
            } catch (e) {
                handleError(e, "Failed to fetch contact notes");
            }
        },
        existInPageList(obj, list) {
            if(list.length==0) return false;
            var i;
            for (i = 0; i < list.length; i++) {
                if(list[i].pk_id === obj.pk_id && list[i].aux_id === obj.aux_id) {
                    return true;
                }
            }
            return false;
        },
        async refresImportRequestList() {
            this.pages = [];
            this.currentPage = 0;
            this.lastPage = 0;
            this.pages.push(this.nextPage);
            await this.searchImportRequests(0);
        },        
    }
}
</script>
